import { useCallback } from 'react';

import { TFileBatchModel } from 'types/file';
import { TPicture } from 'types/picture';

import { removeImageBatch, uploadImageBatch } from '@helpers/imageHelpers';

type TUseBatchImages = {
    syncImages: (
        model: TFileBatchModel,
        entityId: number,
        prevImages: TPicture[],
        nextImages: (TPicture | File)[]
    ) => Promise<TPicture[]>;
    addImages: (model: TFileBatchModel, entityId: number, files: File[]) => Promise<TPicture[]>;
    removeImages: (model: TFileBatchModel, entityId: number, ids: number[]) => Promise<void>;
};

export const useBatchImages = (
    onProgress?: (percentage: number, file: File) => void
): TUseBatchImages => {
    const syncImages = useCallback(
        async (
            model: TFileBatchModel,
            entityId: number,
            prevImages: TPicture[],
            nextImages: (TPicture | File)[]
        ) => {
            const newFiles = nextImages.filter((img): img is File => img instanceof File);

            const nextIds = new Set(
                nextImages.map((img) => (img as TPicture)?.id).filter(Boolean) as number[]
            );

            const removedIds = prevImages.map((img) => img.id).filter((id) => !nextIds.has(id));

            if (newFiles.length) {
                await uploadImageBatch(model, entityId, newFiles, onProgress);
            }

            if (removedIds.length) {
                await removeImageBatch(model, entityId, removedIds);
            }

            return nextImages.filter((img): img is TPicture => !(img instanceof File));
        },
        [onProgress]
    );

    const addImages = useCallback(
        async (model: TFileBatchModel, entityId: number, files: File[]) => {
            if (!files.length) return [];
            return await uploadImageBatch(model, entityId, files, onProgress);
        },
        [onProgress]
    );

    const removeImages = useCallback(
        async (model: TFileBatchModel, entityId: number, ids: number[]) => {
            if (!ids.length) return;
            await removeImageBatch(model, entityId, ids);
        },
        []
    );

    return { syncImages, addImages, removeImages };
};
